import { Injectable } from '@angular/core';
import {
  catchError,
  concat,
  Observable,
  BehaviorSubject,
  from,
  switchMap,
} from 'rxjs';
import { environment } from '../../environments/environment';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
} from '@angular/common/http';
import { handleErrorFunction } from '../shared/helpers/handleError';
import { ToastService } from './toast.service';
import { EntraService } from './entra.service';
import { AssetsDataService } from './assets-data.service';
import { DataLoading } from '../models/DataLoading.interface';
import { LogEntryPhoto } from '../models/logs/photos/LogEntryPhoto.interface';
import { ScreenSizeService } from './screen-size.service';
import { convertPdfToImage } from '../helpers/convert-pdf-to-image';
import { PhotoMetadata } from '../models/logs/photos/PhotoMetadata';
import { LogPhotoResponse } from '../models/logs/photos/LogPhotoResponse';
import { convertAzurePhotosToLogEntryPhotos, convertPhotosToFiles } from '../shared/helpers/photos-utils';
import { ToastText } from '../models/assets-data/UiConfig.interface';
import { LoadingText } from '../models/assets-data/UiConfig.interface';
import { LogEntryPhotoFile, LogEntryPhotoFilesResponse } from '../models/logs/photos/LogEntryPhotoFiles.interface';
import { copyPhotosToClipboard, processPhotosForClipboard } from '../helpers/copy-photos';

/**
 * How the backend should file the photos in one upload request. Omitted for
 * ordinary shipment proof photos, which are the endpoint's default.
 */
export type PhotoUploadKind = 'Slip' | 'Damage';

/**
 * One `upload-photo-batch` request's worth of photos.
 *
 * `files` is a single flat field on that endpoint (unlike the submit path, which
 * splits into `Photos`/`SlipPhotos`/`DamagePhotos`), so `kind` applies to every
 * photo in the batch — a mixed upload has to be several batches, one per kind.
 */
export interface PhotoUploadBatch {
  photos: LogEntryPhoto[];
  /** Omit for proof photos. */
  kind?: PhotoUploadKind;
  /**
   * Damage batches only: the receipt rows these photos attach to, in the order
   * the photo names index into. A photo named `damage-{position}-{n}` attaches to
   * `receiptIds[position]` — a 0-based position in THIS array, not a receipt id.
   * Build the array and the names in one pass from one source, or photos land on
   * the wrong PO line.
   */
  receiptIds?: number[];
}

@Injectable({
  providedIn: 'root',
})
export class PhotoService {
  private _httpClient: HttpClient;
  private _toastService: ToastService;
  private _entraService: EntraService;
  private _assetsDataService: AssetsDataService;
  private _screenSizeService: ScreenSizeService;

  public dataLoadingDefault = { isLoading: false, message: '' };
  public dataLoading = new BehaviorSubject<DataLoading>(this.dataLoadingDefault);

  private logEntryPhotosSubject = new BehaviorSubject<LogEntryPhoto[]>([]);
  logEntryphotos$ = this.logEntryPhotosSubject.asObservable();
  
  private lastLogEntryPhotosSubject = new BehaviorSubject<LogEntryPhoto[]>([]);
  lastLogEntryPhotos$ = this.lastLogEntryPhotosSubject.asObservable();

  private logRowPhotosSubject = new BehaviorSubject<PhotoMetadata[]>([]);
  logRowPhotos$ = this.logRowPhotosSubject.asObservable();

  /**
   * A log's `Kind='Damage'` photos, which the photos endpoint returns in their own
   * `damagedPhotos` field. Kept apart from `logRowPhotos$` rather than merged:
   * damage photos are stored separately from a log's shipment photos (they hang
   * off a receipt row and have their own endpoints), so a list that feeds
   * delete-by-index and download-all must not silently contain them.
   */
  private logRowDamagedPhotosSubject = new BehaviorSubject<PhotoMetadata[]>([]);
  logRowDamagedPhotos$ = this.logRowDamagedPhotosSubject.asObservable();

  public isMobile: boolean = false;

  public loadingText: LoadingText;
  public toastText: ToastText;

  constructor(
    private http: HttpClient,
    toastService: ToastService,
    entraService: EntraService,
    assetsDataService: AssetsDataService,
    screenSizeService: ScreenSizeService
  ) {
    this._httpClient = http;
    this._toastService = toastService;
    this._entraService = entraService;
    this._assetsDataService = assetsDataService;
    this._screenSizeService = screenSizeService;

    this._screenSizeService.isMobile.subscribe(isMobile => {
      this.isMobile = isMobile;
    });

    const uiConfig = this._assetsDataService.GetUiConfigData();
    this.loadingText = uiConfig.loadingText;
    this.toastText = uiConfig.toastText;
  }

  getLogEntryPhotos(): LogEntryPhoto[] {
    return this.logEntryPhotosSubject.value;
  }

  updateLogEntryPhotos(photos: LogEntryPhoto[]) {
    this.logEntryPhotosSubject.next([...photos]);
  }

  clearLogEntryPhotos() {
    this.logEntryPhotosSubject.next([]);
  }

  updateLastLogEntryPhotos() {
    this.lastLogEntryPhotosSubject.next([...this.logEntryPhotosSubject.value]);
  }

  getLastLogEntryPhotos(): LogEntryPhoto[] {
    return this.lastLogEntryPhotosSubject.value;
  }

  useLastLogEntryPhotos() {
    this.updateLogEntryPhotos(this.lastLogEntryPhotosSubject.value);
  }

  getLogRowPhotos(): PhotoMetadata[] {
    return this.logRowPhotosSubject.value;
  }

  updateLogRowPhotos(photos: PhotoMetadata[]) {
    this.logRowPhotosSubject.next([...photos]);
  }

  clearLogRowPhotos() {
    this.logRowPhotosSubject.next([]);
    this.logRowDamagedPhotosSubject.next([]);
  }

  getLogRowDamagedPhotos(): PhotoMetadata[] {
    return this.logRowDamagedPhotosSubject.value;
  }

  updateLogRowDamagedPhotos(photos: PhotoMetadata[]) {
    this.logRowDamagedPhotosSubject.next([...photos]);
  }




  /**
   * Downscales and re-encodes a picked image.
   *
   * The defaults are the shipping values for every receiving photo: 1280x720 at
   * 0.7. These photos document that a shipment arrived and what condition it was
   * in — legible at a glance on a phone or in an email — and the payload is
   * uploaded inside the submit request over warehouse Wi-Fi that has measured
   * around 140 kbps. The previous 1920x1080/0.8-0.95 produced multi-megabyte
   * submits that took minutes to send; this is roughly a third of that.
   */
  private async resizeImage(
    file: File,
    maxWidth: number = 1280,
    maxHeight: number = 720,
    quality: number = 0.7
  ): Promise<LogEntryPhoto> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
  
          // Calculate new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }
  
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);
  
          // Convert to data URL with specified quality
          const dataUrl = canvas.toDataURL('image/jpeg', quality);
          
          const newPhoto: LogEntryPhoto = {
            id: Date.now().toString() + Math.random().toString(36).substring(2, 9),
            url: dataUrl,
            name: file.name
          };
          
          resolve(newPhoto);
        };
  
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };
  
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Validates a picked/captured file and converts it into LogEntryPhoto(s),
   * applying the same PDF handling and mobile/large-file compression used
   * across the app. Returns [] for an invalid file type (and toasts).
   *
   * Shared by the desktop processFile() and the receiving-mobile photo flow so
   * both go through one proven implementation.
   */
  public async buildPhotosFromFile(file: File): Promise<LogEntryPhoto[]> {
    if (!file || (!file.type.startsWith('image/') && file.type !== 'application/pdf')) {
      this._toastService.showWarningMessage(this.toastText.photos.processing.invalidFile);
      return [];
    }

    if (file.type === 'application/pdf') {
      return await convertPdfToImage(file);
    }

    // Heavier compression for mobile / large files; slightly lighter for desktop,
    // where the upload isn't going over the warehouse uplink. Both sit far below
    // the old 1920x1080 — see resizeImage.
    const quality = (this.isMobile || file.size > 5 * 1024 * 1024) ? 0.7 : 0.8;
    return [await this.resizeImage(file, 1280, 720, quality)];
  }

  async processFile(file: File) {
    this.dataLoading.next({ isLoading: true, message: this.loadingText.processingFile });
    try {
      const newPhotos = await this.buildPhotosFromFile(file);
      if (newPhotos.length) {
        this.updateLogEntryPhotos([...newPhotos, ...this.getLogEntryPhotos()]);
      }
      this.dataLoading.next({ isLoading: false, message: '' });
    } catch (error) {
      console.error('Error processing image:', error);
      this._toastService.showWarningMessage(this.toastText.photos.processing.error);
      this.dataLoading.next({ isLoading: false, message: '' });
    }
  }



  /**
 * Fetches photos associated with a log entry
 *
 * @param logEntryId The ID of the log entry to fetch photos for
 * @param onSuccessFunction Optional callback function to execute after successful retrieval
 * @returns An observable of the photo array for components that need direct access
 */
  public getPhotosForLogEntry(
    logEntryId: string,
    onSuccessFunction?: Function
  ): void {
    this.dataLoading.next({
      isLoading: true,
      message: this.loadingText.loadingPhotos
    });
    
    const request = this.getPhotosForLogEntryRequest(logEntryId);
    
    // Create a subscriber that handles the UI feedback
    request.subscribe({
      next: (response: LogPhotoResponse) => {
        if(response.photos && response.photos.length > 0){
          this.updateLogRowPhotos(response.photos);
        } else {
          this.updateLogRowPhotos([]);

        }
        // Damage photos ride along in their own field. Absent for a log with none
        // — and for any backend that predates the field, hence the ?? [].
        this.updateLogRowDamagedPhotos(response.damagedPhotos ?? []);
        if (onSuccessFunction) {
          onSuccessFunction(response.photos);
        }
      
      },
      error: (err: HttpErrorResponse) => {
        console.error(err);
        this._toastService.showWarningMessage(this.toastText.photos.fetch.error);
        this.dataLoading.next(this.dataLoadingDefault);
      },
      complete: () => {
        // Reset the loading state when done
        this.dataLoading.next(this.dataLoadingDefault);
      }
    });
    
  }


  /**
   * Adds multiple photos to a receiving log entry.
   * @param logId The ID of the receiving log entry
   * @param poNumber The purchase order number
   * @param photoFiles The photo files to upload
   * @param userName The username of the person uploading the photos
   * @param onSuccessFunction Optional callback function to execute after successful upload
   */
  public addPhotos(
    logId: number,
    poNumber: string,
    photos: LogEntryPhoto[],
    userName: string,
    onSuccessFunction?: Function
  ): void {
    this.addPhotoBatches(logId, poNumber, [{ photos }], userName, onSuccessFunction);
  }

  /**
   * Adds photos to a log as one request per kind — proof photos unlabelled, slip
   * pages as `kind=Slip`, damage photos as `kind=Damage` with the `receiptIds`
   * they attach to. The endpoint's `kind` covers a whole request, so the split is
   * the only way to upload more than one kind at once.
   *
   * Requests run in sequence rather than together: they write to the same log, and
   * a serial run makes a mid-way failure legible ("the first kind landed") instead
   * of leaving an arbitrary subset. Empty batches are dropped, so callers can pass
   * a fixed set of buckets.
   *
   * The log's photos are re-fetched either way — a partial upload still changed
   * what is stored, and the caller's view must not keep showing the old set.
   *
   * @param logId The receiving log to attach to
   * @param poNumber The purchase order number
   * @param batches Photos grouped by kind; each becomes one request
   * @param userName Who is uploading
   * @param onSuccessFunction Runs once, only if every batch succeeded
   */
  public addPhotoBatches(
    logId: number,
    poNumber: string,
    batches: PhotoUploadBatch[],
    userName: string,
    onSuccessFunction?: Function
  ): void {
    const uploads = batches.filter((batch) => batch.photos.length > 0);
    if (uploads.length === 0) { return; }

    this.dataLoading.next({ isLoading: true, message: this.loadingText.loadingPhotos});

    // Names are what tie a photo to its kind (`packing-slip-n`) and, for damage,
    // to its receipt row (`damage-{position}-{n}`), so the conversion to File
    // objects has to preserve them — convertPhotosToFiles carries photo.name
    // through, and only falls back to a generic name for a photo that has none.
    const requests = uploads.map((batch) =>
      from(convertPhotosToFiles(batch.photos)).pipe(
        switchMap((photoFiles) =>
          this.PostPhotos(logId, poNumber, photoFiles, userName, batch.kind, batch.receiptIds)
        )
      )
    );

    concat(...requests).subscribe({
      error: (err: unknown) => {
        console.error(err);
        // A failure before the request even goes out is a processing problem
        // (unreadable photo data), not a rejected upload.
        this._toastService.showWarningMessage(
          err instanceof HttpErrorResponse
            ? this.toastText.photos.upload.error
            : this.toastText.photos.processing.error
        );
        this.dataLoading.next(this.dataLoadingDefault);
        this.getPhotosForLogEntry(logId.toString());
      },
      complete: () => {
        this._toastService.showSuccessMessage(this.toastText.photos.upload.success);
        if (onSuccessFunction) {
          onSuccessFunction();
        }
        this.dataLoading.next(this.dataLoadingDefault);
        this.getPhotosForLogEntry(logId.toString());
      },
    });
  }

  /**
   * Deletes a photo by its ID.
   * @param photoId The ID of the photo to delete
   * @param onSuccessFunction Optional callback function to execute after successful deletion
   */
  public deletePhoto(
    photoId: number,
    onSuccessFunction?: Function
  ): void {
    this.dataLoading.next({ isLoading: true, message: this.loadingText.deletingPhoto });
    this.DeletePhoto(photoId).subscribe({
      next: () => {
        this._toastService.showSuccessMessage(this.toastText.photos.delete.single.success);
        
        if (onSuccessFunction) {
          onSuccessFunction();
        }
      },
      error: (err: HttpErrorResponse) => {
        console.error(err);
        this._toastService.showWarningMessage(this.toastText.photos.delete.single.error);
        this.dataLoading.next(this.dataLoadingDefault);
      },
      complete: () => {
        this.dataLoading.next(this.dataLoadingDefault);
      },
    });
  }

  /**
   * Deletes all photos for a specific log entry.
   * @param logId The ID of the log entry to delete photos for
   */
  public deleteAllPhotos(logId: number, onSuccessFunction?: Function): void {
    this.DeleteAllPhotos(logId).subscribe({
      next: () => {
        this.dataLoading.next({ isLoading: true, message: this.loadingText.deletingAllPhotos });
        this._toastService.showSuccessMessage(this.toastText.photos.delete.all.success);
        if (onSuccessFunction) {
          onSuccessFunction();
        }
        this.updateLogRowPhotos([]);
      },
      error: (err: HttpErrorResponse) => {
        console.error(err);
        this._toastService.showWarningMessage(this.toastText.photos.delete.all.error);
        this.dataLoading.next(this.dataLoadingDefault);
      },
      complete: () => {
        this.dataLoading.next(this.dataLoadingDefault);
      },
    });
  }


  
/**
 * Fetches the actual file content of photos for a log entry
 * 
 * @param logEntryId The ID of the log entry to fetch photos for
 * @param onSuccessFunction Optional callback function to execute after successful retrieval
 * @returns An observable of the photo files response
 */
public getPhotoFilesForLogEntry(
  logEntryId: number,
  copyToClipboard: boolean = false,
  onSuccessFunction?: Function
): Observable<LogEntryPhotoFilesResponse> {
  // Set loading state
  this.dataLoading.next({
    isLoading: true,
    message: 'Fetching Photo Files'
  });
  
  const request = this.getPhotoFilesForLogEntryRequest(logEntryId);
  
  // Create a subscriber that handles the UI feedback
  request.subscribe({
    next: (response: LogEntryPhotoFilesResponse) => {
      if (copyToClipboard) {
        console.log('Copying photos to clipboard');
        processPhotosForClipboard(response.files);
      }
      if (onSuccessFunction) {
        onSuccessFunction(response.files);
      }
    },
    error: (err: HttpErrorResponse) => {
      console.error(err);
      this._toastService.showWarningMessage(
        err.error?.message || 'Failed to retrieve photo files'
      );
  
      this.dataLoading.next(this.dataLoadingDefault);
    },
    complete: () => {
      // Reset the loading state when done
      this.dataLoading.next(this.dataLoadingDefault);
    }
  });
  
  // Return the original observable for components that want to use it directly
  return request;
}





//------HTTP REQUESTS------

        /**
 * HTTP Request to fetch photos for a specific log entry
 * 
 * @param logEntryId The ID of the log entry to fetch photos for
 * @returns An observable that resolves to an array of photo metadata
 */
public getPhotosForLogEntryRequest(
  logEntryId: string
): Observable<LogPhotoResponse> {
  return from(this._entraService.acquireToken()).pipe(
    switchMap((accessToken) => {
      const url = `${environment.apiUrl}/V1/photos/log/${logEntryId}`;
      const headers: HttpHeaders = new HttpHeaders({
        Authorization: `Bearer ${accessToken}`
      });
      
      return this._httpClient.get<LogPhotoResponse>(
        url,
        { headers }
      ).pipe(
        catchError((error) => handleErrorFunction(error))
      );
    })
  );
}




  /**
   * HTTP Request to fetch damaged-item photos for a specific log entry.
   * Mirrors getPhotosForLogEntryRequest exactly, against the separate
   * damaged-item-photos endpoint — damage photos are stored apart from a log's
   * regular shipment photos, and the log-edit screen's damage panel needs both
   * read read-only, side by side (handoff-18 §1.2: "photos stay on their
   * existing endpoints", unchanged by the new details payload).
   *
   * @param logEntryId The ID of the log entry to fetch damaged-item photos for
   * @returns An observable that resolves to the damaged-item photo array
   */
  public getDamagedItemPhotosForLogRequest(
    logEntryId: string
  ): Observable<LogPhotoResponse> {
    return from(this._entraService.acquireToken()).pipe(
      switchMap((accessToken) => {
        const url = `${environment.apiUrl}/V1/damaged-item-photos/log/${logEntryId}`;
        const headers: HttpHeaders = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`
        });

        return this._httpClient.get<LogPhotoResponse>(
          url,
          { headers }
        ).pipe(
          catchError((error) => handleErrorFunction(error))
        );
      })
    );
  }

  /**
   * HTTP Request to upload multiple photos for a receiving log entry.
   * @param logId The ID of the receiving log entry
   * @param poNumber The purchase order number
   * @param photoFiles The photo files to upload
   * @param userName The username of the person uploading the photos
   * @returns An observable that resolves to the response containing the result of the upload
   */
  private PostPhotos(
    logId: number,
    poNumber: string,
    photoFiles: File[],
    userName: string,
    kind?: PhotoUploadKind,
    receiptIds?: number[]
  ): Observable<any> {
    return from(this._entraService.acquireToken()).pipe(
      switchMap((accessToken) => {
        const url = `${environment.apiUrl}/V1/photos/upload-photo-batch`;
        const headers: HttpHeaders = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`,
        });

        const formData = new FormData();
        photoFiles.forEach((file, index) => {
          // The file name is part of the contract, not decoration: it carries the
          // page number for a slip and the receipt position for damage, so it is
          // appended explicitly rather than left to the File's own name.
          formData.append('files', file, file.name);
        });
        formData.append('logEntryId', logId.toString());
        formData.append('poNumber', poNumber);
        formData.append('userName', userName);
        // Omitted entirely for proof photos — the endpoint's default.
        if (kind) {
          formData.append('kind', kind);
        }
        // Repeated field: `damage-{position}-{n}` indexes into this array by
        // position, so its order is the contract.
        if (receiptIds && receiptIds.length > 0) {
          receiptIds.forEach((receiptId) => {
            formData.append('receiptIds', receiptId.toString());
          });
        }

        return this._httpClient
          .post(url, formData, { headers })
          .pipe(catchError((error) => handleErrorFunction(error)));
      })
    );
  }

  /**
   * HTTP Request to delete a photo by its ID.
   * @param photoId The ID of the photo to delete
   * @returns An observable that resolves to the response containing the result of the deletion
   */
  private DeletePhoto(photoId: number): Observable<any> {
    return from(this._entraService.acquireToken()).pipe(
      switchMap((accessToken) => {
        const url = `${environment.apiUrl}/V1/photos/${photoId}`;
        const headers: HttpHeaders = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`,
        });

        return this._httpClient
          .delete(url, { headers })
          .pipe(catchError((error) => handleErrorFunction(error)));
      })
    );
  }

  /**
   * HTTP Request to delete all photos for a receiving log entry. 
   * @param logId The ID of the receiving log entry
   * @returns An observable that resolves to the response containing the result of the deletion
   */
  private DeleteAllPhotos(logId: number): Observable<any> {
    return from(this._entraService.acquireToken()).pipe(
      switchMap((accessToken) => {
        const url = `${environment.apiUrl}/V1/photos/log/${logId}`;
        const headers: HttpHeaders = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`,
        });

        return this._httpClient
          .delete(url, { headers })
          .pipe(catchError((error) => handleErrorFunction(error)));
      })
    );
  }




  /**
 * HTTP Request to fetch photo files for a specific log entry
 * 
 * @param logEntryId The ID of the log entry to fetch photo files for
 * @returns An observable that resolves to photo files with content
 */
private getPhotoFilesForLogEntryRequest(
  logEntryId: number
): Observable<LogEntryPhotoFilesResponse> {
  return from(this._entraService.acquireToken()).pipe(
    switchMap((accessToken) => {
      const url = `${environment.apiUrl}/V1/photos/files/${logEntryId}`;
      const headers: HttpHeaders = new HttpHeaders({
        Authorization: `Bearer ${accessToken}`
      });
      
      return this._httpClient.get<LogEntryPhotoFilesResponse>(
        url,
        { headers }
      ).pipe(
        catchError((error) => handleErrorFunction(error))
      );
    })
  );
}

} 